import React, { useMemo } from 'react';
import {
  MembersPanel,
  MembersHeader,
  MembersGroupName,
  MembersCount,
  MembersStatRow,
  MembersStat,
  MembersListTitle,
  MemberRow,
  MemberInfo,
  CoachBadge,
  Avatar,
  PresenceDot,
} from './styles';
import { initialsOf, avatarColor, isOnlineFromLastSeen, lastSeenLabel } from './helpers';

// Members sidebar for the group chat. Built from the program (coach + participants),
// since the chat API doesn't carry the roster. Shows colored avatars, per-member
// online dot, a Coach badge, and group stats.
const GroupMembersPanel = ({ program, conversation }) => {
  const members = useMemo(() => {
    const list = [];
    const coach = program?.coach || program?.coachProgram?.coach;
    if (coach) {
      list.push({
        key: coach.user?.uid || coach.uid,
        name: coach.fullName,
        avatar: (coach.profileImage || {}).compressedUrl || (coach.profileImage || {}).url,
        lastSeenAt: coach.user?.lastSeenAt,
        isCoach: true,
      });
    }
    const participants = program?.coachProgram?.participants || [];
    participants.forEach((p) => {
      list.push({
        key: p.user?.uid || p.uid,
        name: p.fullName,
        avatar: (p.avatar || {}).compressedUrl || (p.avatar || {}).url,
        lastSeenAt: p.user?.lastSeenAt,
        isCoach: false,
      });
    });
    return list;
  }, [program]);

  const onlineCount = members.filter((m) => isOnlineFromLastSeen(m.lastSeenAt)).length;

  return (
    <MembersPanel>
      <MembersHeader>
        <MembersGroupName>{program?.name || conversation?.user?.name || 'Group'}</MembersGroupName>
        <MembersCount>
          {members.length} {members.length === 1 ? 'member' : 'members'}
          {onlineCount > 0 ? ` · ${onlineCount} online` : ''}
        </MembersCount>
      </MembersHeader>

      <MembersStatRow>
        <MembersStat>
          <div className="value">{members.length}</div>
          <div className="label">Members</div>
        </MembersStat>
        <MembersStat>
          <div className="value">{conversation?.messageCount ?? 0}</div>
          <div className="label">Messages</div>
        </MembersStat>
        <MembersStat>
          <div className="value">{conversation?.unreadMessageCount || 0}</div>
          <div className="label">Unread</div>
        </MembersStat>
      </MembersStatRow>

      <MembersListTitle>Members</MembersListTitle>
      {members.map((m) => {
        const online = isOnlineFromLastSeen(m.lastSeenAt);
        const c = avatarColor(m.key || m.name);
        return (
          <MemberRow key={m.key || m.name}>
            <Avatar src={m.avatar} bg={c.bg} fg={c.fg} size="3.8rem">
              {!m.avatar && initialsOf(m.name)}
              <PresenceDot online={online} />
            </Avatar>
            <MemberInfo online={online}>
              <div className="name">{m.name || 'Member'}</div>
              <div className="status">{online ? 'Online' : lastSeenLabel(m.lastSeenAt)}</div>
            </MemberInfo>
            {m.isCoach && <CoachBadge>Coach</CoachBadge>}
          </MemberRow>
        );
      })}
    </MembersPanel>
  );
};

export default GroupMembersPanel;
